/**
 * Wayfarer's site header — replaces the template's monogram + name with the
 * owner's supplied lockup, which already contains the brand name, so printing
 * the name beside it would say "Wayfarer" twice.
 *
 * The shell mounts this bare inside `sticky top-0`. The header keeps the
 * content column's width, floats 30px below the viewport top, and owns a
 * single pill-shaped menu with the same gradient ring as the kickoff choices.
 * Each supplied navigation item is presented as a numbered journey step. The
 * active step follows the current agent surface's data marker, while still
 * respecting an explicit active flag from the shell.
 *
 * The mobile treatment is ours to provide — the shell has none: the inline nav
 * hides below `sm`, a hamburger opens a right-side sidebar portaled to
 * `document.body`, and Escape closes.
 *
 * Localized nav labels, intents and menu ARIA wording come from the shell and
 * the platform message catalogue; the lockup carries the untranslated name.
 */
import { CircleUserRound, Mail, Menu, X } from 'lucide-react';
import { useEffect, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';

import { TravellerDashboardDrawer } from '@/app/agent/TravellerDashboardDrawer.tsx';
import { WayfarerLogo } from '@/app/agent/WayfarerLogo.tsx';
import { messages } from '@/app/lib/localization/messages.ts';
import type { AgentHeaderProps } from '@/app/lib/stage/StageHeader.tsx';
import type { NavItem } from '@/app/lib/stage/nav-model.ts';
import { cn } from '@/app/lib/utils';

const headerMessages = defineMessages({
  contactLabel: {
    id: 'site.header.contact',
    defaultMessage: 'Contact',
    description: 'Accessible label for the Wayfarer contact action',
  },
  contactIntent: {
    id: 'site.header.contactIntent',
    defaultMessage: 'I want to contact Wayfarer',
    description: 'Message sent when the visitor opens the contact action',
  },
  dashboardLabel: {
    id: 'site.header.dashboard',
    defaultMessage: 'Dashboard',
    description: 'Accessible label for the traveller dashboard action',
  },
  logoHomeLabel: {
    id: 'site.header.logoHome',
    defaultMessage: 'Go to {brandName} home',
    description: 'Accessible label for the Wayfarer logo home action',
  },

});

const NAV_ITEM_CLASS =
  'group flex flex-none cursor-pointer touch-manipulation items-center gap-2 whitespace-nowrap rounded-full border-none bg-transparent px-2.5 py-1.5 text-xs font-semibold uppercase tracking-caps text-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary data-[active=true]:bg-primary/10 sm:px-3 sm:py-2';

const STEP_CIRCLE_CLASS =
  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold tabular-nums transition-colors sm:h-7 sm:w-7 sm:text-xs';

const MENU_ACTION_CLASS =
  'grid h-9 w-9 shrink-0 cursor-pointer touch-manipulation place-items-center rounded-full border-none bg-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary';

const MENU_PILL_STYLE = {
  backgroundColor: 'var(--wayfarer-menu-surface, hsl(var(--glass)))',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
};

const MENU_DRAWER_STYLE = {
  backgroundColor: 'var(--wayfarer-menu-surface, hsl(var(--card) / 0.55))',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
};

const MENU_BORDER_STYLE = {
  background: 'hsl(var(--muted-foreground) / 0.18)',
  boxSizing: 'border-box' as const,
  padding: '1px',
  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  WebkitMaskComposite: 'xor',
  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
  maskComposite: 'exclude',
};

const MENU_ACCENT_STYLE = {
  background:
    'linear-gradient(to right, hsl(var(--primary) / 0) 0%, hsl(var(--primary)) 50%, hsl(var(--primary) / 0) 100%)',
};

export const SiteHeader: FC<AgentHeaderProps> = ({ brandName, navItems, onNavigate }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [availableThrough, setAvailableThrough] = useState(2);
  const intl = useIntl();

  const contactItem: NavItem = {
    label: intl.formatMessage(headerMessages.contactLabel),
    intent: intl.formatMessage(headerMessages.contactIntent),
  };
  const dashboardLabel = intl.formatMessage(headerMessages.dashboardLabel);

  useEffect(() => {
    if (!menuOpen && !dashboardOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setDashboardOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dashboardOpen, menuOpen]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }

    const readActiveStep = (): void => {
      const contactVisible = document.querySelector('[data-wayfarer-contact]') !== null;
      setContactOpen((current) => (current === contactVisible ? current : contactVisible));
      if (contactVisible) {
        setActiveStep((current) => (current === 0 ? current : 0));
        return;
      }

      const markers = document.querySelectorAll<HTMLElement>('[data-wayfarer-step]');
      const marker = markers.item(markers.length - 1);
      const markerStep = Number.parseInt(marker?.dataset.wayfarerStep ?? '', 10);
      const markerAvailableThrough = Number.parseInt(
        marker?.dataset.wayfarerStepAvailableThrough ?? '',
        10,
      );
      const explicitStep = navItems.findIndex((item) => item.active === true) + 1;
      const nextStep = markerStep >= 1 ? markerStep : explicitStep > 0 ? explicitStep : 1;
      const nextAvailableThrough =
        markerAvailableThrough >= 1
          ? Math.max(markerAvailableThrough, nextStep)
          : Math.max(nextStep, 2);
      setActiveStep((current) => (current === nextStep ? current : nextStep));
      setAvailableThrough((current) =>
        current === nextAvailableThrough ? current : nextAvailableThrough,
      );
    };

    readActiveStep();
    const observer = new MutationObserver(readActiveStep);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        'data-wayfarer-contact',
        'data-wayfarer-step',
        'data-wayfarer-step-available-through',
      ],
    });
    return () => observer.disconnect();
  }, [navItems]);

  const handleItemClick = (item: NavItem): void => {
    setMenuOpen(false);
    onNavigate(item);
  };

  const handleLogoClick = (): void => {
    const homeItem = navItems[0];
    if (homeItem) {
      handleItemClick(homeItem);
    }
  };

  return (
    <header className="flex-none bg-transparent px-5 pt-[30px] md:px-20">
      <div
        className="relative mx-auto flex w-full max-w-container-content items-center gap-x-3 overflow-hidden rounded-full border-0 px-4 py-3 sm:gap-x-4 sm:px-5 sm:py-4"
        data-wayfarer-menu="true"
        style={MENU_PILL_STYLE}
      >
        <span
          className="pointer-events-none absolute inset-0 z-0 rounded-full"
          style={MENU_BORDER_STYLE}
          aria-hidden="true"
        />
        <span
          className="pointer-events-none absolute inset-x-5 top-0 z-0 h-px rounded-full sm:inset-x-6"
          style={MENU_ACCENT_STYLE}
          aria-hidden="true"
        />
        <button
          type="button"
          className="relative z-10 flex flex-none cursor-pointer touch-manipulation items-center border-none bg-transparent p-0 text-foreground transition-transform hover:scale-[1.02] active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
          aria-label={intl.formatMessage(headerMessages.logoHomeLabel, { brandName })}
          onClick={handleLogoClick}
        >
          <WayfarerLogo className="h-6 w-auto sm:h-[1.85rem]" />
        </button>

        <nav
          className="scrollbar-none relative z-10 hidden min-w-0 items-center gap-0.5 overflow-x-auto sm:ml-auto sm:flex sm:gap-1"
          aria-label={intl.formatMessage(messages.siteNavigation)}
        >
          {navItems.map((item, index) => {
            const stepNumber = index + 1;
            const active = !contactOpen && (item.active ?? activeStep === stepNumber);
            const locked = stepNumber > availableThrough && !active;
            return (
              <button
                key={item.label}
                type="button"
                className={cn(NAV_ITEM_CLASS, locked && 'cursor-not-allowed opacity-50 hover:bg-transparent')}
                data-active={active}
                aria-current={active ? 'step' : undefined}
                aria-disabled={locked || undefined}
                disabled={locked}
                onClick={() => {
                  if (!locked) {
                    onNavigate(item);
                  }
                }}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    STEP_CIRCLE_CLASS,
                    active
                      ? 'border-primary bg-primary text-primary-foreground shadow-[0_0_0_3px_hsl(var(--primary)/0.16)]'
                      : 'border-muted-foreground/30 text-foreground group-hover:border-primary/60 group-hover:text-primary',
                  )}
                >
                  {stepNumber}
                </span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="relative z-10 hidden shrink-0 items-center gap-1 border-l border-muted-foreground/20 pl-2 sm:flex">
          <button
            type="button"
            className={MENU_ACTION_CLASS}
            aria-label={contactItem.label}
            title={contactItem.label}
            onClick={() => onNavigate(contactItem)}
          >
            <Mail className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={MENU_ACTION_CLASS}
            aria-label={dashboardLabel}
            title={dashboardLabel}
            onClick={() => setDashboardOpen(true)}
          >
            <CircleUserRound className="h-[20px] w-[20px]" aria-hidden="true" />
          </button>
        </div>

        {navItems.length > 0 && (
          <button
            type="button"
            className="relative z-10 ml-auto grid h-9 w-9 cursor-pointer touch-manipulation place-items-center rounded-md border-none bg-transparent text-foreground transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary sm:hidden"
            aria-label={intl.formatMessage(messages.openMenu)}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
      </div>

      {createPortal(
        <>
          {dashboardOpen ? <TravellerDashboardDrawer onClose={() => setDashboardOpen(false)} /> : null}
          <div
            aria-hidden="true"
            className={cn(
              'fixed inset-0 z-[11] bg-foreground/25 backdrop-blur-[20px] transition-opacity duration-[220ms] sm:hidden',
              menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
            onPointerDown={() => setMenuOpen(false)}
          />
          <div
            className={cn(
              'fixed inset-y-0 right-0 z-[12] flex w-72 flex-col border-l border-muted-foreground/20 shadow-lift transition-transform duration-[220ms] ease-in-out sm:hidden',
              menuOpen ? 'translate-x-0' : 'translate-x-full',
            )}
            data-wayfarer-menu-drawer="true"
            style={MENU_DRAWER_STYLE}
          >
            <div className="flex items-center justify-between py-3 pl-5 pr-3">
              <button
                type="button"
                className="flex cursor-pointer touch-manipulation items-center border-none bg-transparent p-0 text-foreground transition-transform hover:scale-[1.02] active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                aria-label={intl.formatMessage(headerMessages.logoHomeLabel, { brandName })}
                onClick={handleLogoClick}
              >
                <WayfarerLogo className="h-6 w-auto" />
              </button>
              <button
                type="button"
                className="grid h-9 w-9 cursor-pointer touch-manipulation place-items-center rounded-md border-none bg-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                aria-label={intl.formatMessage(messages.closeMenu)}
                onClick={() => setMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav
              className="flex flex-col gap-1 px-3 pb-4"
              aria-label={intl.formatMessage(messages.siteNavigation)}
            >
              {navItems.map((item, index) => {
                const stepNumber = index + 1;
                const active = !contactOpen && (item.active ?? activeStep === stepNumber);
                const locked = stepNumber > availableThrough && !active;
                return (
                  <button
                    key={item.label}
                    type="button"
                    className={cn(
                      'group flex cursor-pointer touch-manipulation items-center gap-3 rounded-full border-none bg-transparent px-3 py-3 text-left text-xs font-semibold uppercase tracking-caps text-white transition-colors hover:bg-muted hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary data-[active=true]:bg-primary/10',
                      locked && 'cursor-not-allowed text-white/50 hover:bg-transparent',
                    )}
                    data-active={active}
                    aria-current={active ? 'step' : undefined}
                    aria-disabled={locked || undefined}
                    disabled={locked}
                    tabIndex={menuOpen ? 0 : -1}
                    onClick={() => {
                      if (!locked) {
                        handleItemClick(item);
                      }
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        STEP_CIRCLE_CLASS,
                        active
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border text-muted-foreground group-hover:border-primary/60 group-hover:text-primary',
                      )}
                    >
                      {stepNumber}
                    </span>
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
            <div className="mt-auto grid grid-cols-2 gap-2 border-t border-muted-foreground/20 px-3 py-4">
              <button
                type="button"
                className="flex cursor-pointer touch-manipulation items-center justify-center gap-2 rounded-full border border-muted-foreground/20 bg-transparent px-3 py-3 text-[10px] font-semibold uppercase tracking-caps text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                onClick={() => handleItemClick(contactItem)}
              >
                <Mail className="h-[18px] w-[18px]" aria-hidden="true" />
                <span>{contactItem.label}</span>
              </button>
              <button
                type="button"
                className="flex cursor-pointer touch-manipulation items-center justify-center gap-2 rounded-full border border-muted-foreground/20 bg-transparent px-3 py-3 text-[10px] font-semibold uppercase tracking-caps text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                onClick={() => {
                  setMenuOpen(false);
                  setDashboardOpen(true);
                }}
              >
                <CircleUserRound className="h-[18px] w-[18px]" aria-hidden="true" />
                <span>{dashboardLabel}</span>
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </header>
  );
};
